/**
 * Shared helpers for estimating the size of a detected leak from a
 * leak_detected notification's stored data.
 *
 * Leak notifications (see server previousDayLeakService) store:
 *  - data.hourlyUsage: number[] of adjusted hourly usage (litres) for the day
 *  - data.nightHours: number[] of night-window hour indices analysed
 *  - data.continuousNightHours: longest continuous night-flow streak (hours)
 *
 * A genuine leak is a constant background flow: it doesn't spike up and down
 * hour to hour the way real usage does, so it will either hold steady or
 * grow over time. Averaging the night-window hours can overstate the leak
 * because it also pulls in incidental usage that happened during the night.
 * Instead we take the single lowest hourly usage value in the configured
 * period that still meets the configured flow threshold.
 */

/**
 * Estimate the sustained leak rate in litres per hour.
 * Uses the lowest hourly usage value across the night window, since a leak
 * is a steady flow rather than something that spikes, so the minimum
 * reading is the best approximation of the true leak rate.
 * @param {Object} notification
 * @returns {number|null} litres/hour, or null if it can't be estimated
 */
export function estimateLeakLitresPerHour(notification) {
  const data = notification?.data || {};
  const hourly = Array.isArray(data.hourlyUsage) ? data.hourlyUsage : [];
  const nightHours = Array.isArray(data.nightHours) ? data.nightHours : [];
  if (!hourly.length || !nightHours.length) return null;
  const threshold = Number(data.nightFlowThreshold);
  const hasThreshold = Number.isFinite(threshold) && threshold > 0;

  const values = [];
  nightHours.forEach((h) => {
    const v = Number(hourly[h]);
    if (!Number.isFinite(v) || v < 0) return;
    if (hasThreshold && v < threshold) return;
    values.push(v);
  });
  if (!values.length) return null;

  const rate = Math.min(...values);
  return rate > 0 ? rate : null;
}

export function formatLeakMessageWithSize(notification) {
  const message = String(notification?.message || 'Leak detected').trim();
  if (notification?.type !== 'leak_detected') return message;

  const hasPerDayLeakSize = /l\s*\/\s*day|lit(er|re)s?\s*\/\s*day/i.test(message);
  const hasLeakPerHourInMessage = /l\s*\/\s*(hour|hr)|lit(er|re)s?\s*\/\s*(hour|hr)/i.test(message);
  if (hasLeakPerHourInMessage && !hasPerDayLeakSize) return message;

  const estLitresPerHour = estimateLeakLitresPerHour(notification);
  const leakSizeText = estLitresPerHour != null
    ? `~${Number(estLitresPerHour).toLocaleString('en-US')} L/hour`
    : 'size unavailable';

  if (hasPerDayLeakSize) {
    // Normalize historical per-day leak text to per-hour wording.
    return message.replace(
      /leak\s*size\s*.*?(l\s*\/\s*day|lit(er|re)s?\s*\/\s*day)/i,
      `possible leak ${leakSizeText}`
    );
  }

  return `${message} (Possible Leak: ${leakSizeText})`;
}
